// Developer: Ankit Gupta
/**
 * The night sky behind the top bar on Orion's screens (24 Oct 2026): a faint field of stars and
 * the constellation Orion (Betelgeuse, Bellatrix, the belt of Alnitak, Alnilam and Mintaka, Saiph
 * and Rigel, with Meissa at the head). While the cursor moves over the bar the stars drift with it,
 * the near ones more than the far, and the constellation's lines brighten; they settle when it
 * leaves. A few stars twinkle slowly. Nothing moves for someone who asked for reduced motion.
 *
 * Drawn once in SVG; the movement is two CSS variables on the bar, set without re-rendering.
 */
import { useEffect, useMemo, useRef } from 'react';

interface Star { x: number; y: number; r: number; layer: 0 | 1 | 2; twinkle: boolean }

// the same sky every time: a small seeded generator, not Math.random
function sky(count: number): Star[] {
  let seed = 7;
  const next = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
  return Array.from({ length: count }, () => {
    const layer = (next() < 0.55 ? 0 : next() < 0.7 ? 1 : 2) as Star['layer'];
    return { x: next() * 100, y: 3 + next() * 42, r: [0.5, 0.8, 1.1][layer] * (0.8 + next() * 0.4), layer, twinkle: next() < 0.18 };
  });
}

// Orion as seen from the ground, in a 72 x 44 box; brightness sets the size
const ORION = {
  meissa: [34, 3, 1.0], betelgeuse: [14, 8, 2.0], bellatrix: [52, 11, 1.5],
  alnitak: [27, 23, 1.3], alnilam: [34, 22, 1.4], mintaka: [41, 21, 1.2],
  saiph: [19, 40, 1.3], rigel: [57, 38, 1.9],
} as const;
type Name = keyof typeof ORION;
const LINES: [Name, Name][] = [
  ['betelgeuse', 'meissa'], ['meissa', 'bellatrix'], ['betelgeuse', 'alnitak'], ['bellatrix', 'mintaka'],
  ['alnitak', 'alnilam'], ['alnilam', 'mintaka'], ['alnitak', 'saiph'], ['mintaka', 'rigel'],
];

export function OrionSky() {
  const ref = useRef<HTMLDivElement>(null);
  const stars = useMemo(() => sky(90), []);
  useEffect(() => {
    const el = ref.current;
    const bar = el?.parentElement;
    if (!el || !bar) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still) return;
    let frame = 0;
    const move = (e: MouseEvent) => {
      const box = bar.getBoundingClientRect();
      const px = ((e.clientX - box.left) / box.width - 0.5) * 2; // -1 .. 1
      const py = ((e.clientY - box.top) / box.height - 0.5) * 2;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        el.style.setProperty('--sky-x', px.toFixed(3));
        el.style.setProperty('--sky-y', py.toFixed(3));
        el.classList.add('is-awake');
      });
    };
    const leave = () => {
      cancelAnimationFrame(frame);
      el.style.setProperty('--sky-x', '0');
      el.style.setProperty('--sky-y', '0');
      el.classList.remove('is-awake');
    };
    bar.addEventListener('mousemove', move);
    bar.addEventListener('mouseleave', leave);
    return () => { cancelAnimationFrame(frame); bar.removeEventListener('mousemove', move); bar.removeEventListener('mouseleave', leave); };
  }, []);
  return (
    <div ref={ref} className="dp-sky" aria-hidden="true">
      <svg width="100%" height="100%">
        {[0, 1, 2].map((layer) => (
          <g key={layer} className={`dp-sky-layer is-${layer}`}>
            {stars.filter((s) => s.layer === layer).map((s, i) => (
              <circle key={i} cx={`${s.x}%`} cy={s.y} r={s.r} className={s.twinkle ? 'is-twinkle' : undefined}
                style={s.twinkle ? { animationDelay: `${(i * 0.7) % 6}s` } : undefined} />
            ))}
          </g>
        ))}
        <g className="dp-sky-layer is-orion">
          <svg x="64%" y="2" width="72" height="44" viewBox="0 0 72 44" overflow="visible">
            {LINES.map(([a, b]) => (
              <line key={`${a}-${b}`} x1={ORION[a][0]} y1={ORION[a][1]} x2={ORION[b][0]} y2={ORION[b][1]} className="dp-sky-line" />
            ))}
            {(Object.keys(ORION) as Name[]).map((n) => (
              <circle key={n} cx={ORION[n][0]} cy={ORION[n][1]} r={ORION[n][2]}
                className={`dp-sky-star${n === 'betelgeuse' ? ' is-red' : n === 'rigel' ? ' is-blue' : ''}`} />
            ))}
          </svg>
        </g>
      </svg>
    </div>
  );
}
